import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CategoryIcon from '@mui/icons-material/Category';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { ProductListFilterAPICall } from '../../../API/Product List/productListAPI';
import './mobileReports.css';

const MobileProductList = ({ open, onClose, branchId: propBranchId, branchName: propBranchName }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const printRef = useRef(null);
  const decimalPosition = Number(localStorage.getItem("decimalPosition")) || 2;
  const userInfo = useSelector((state) => state.loginResponseSlice.value);
  const allProductsList = useSelector((state) => state.allProductsListSlice.value);
  const allBranchesList = useSelector((state) => state.allBranchesSlice.value);
  const companyProfile = useSelector((state) => state.companyProfileSlice?.userValue);

  // Use prop branchId if provided, otherwise fall back to userInfo
  const effectiveBranchId = propBranchId || userInfo?.branchPk;

  useEffect(() => {
    if (open) {
      // Set default branch from prop or userInfo
      if (propBranchId) {
        const propBranch = allBranchesList?.find(b => b._id === propBranchId);
        setSelectedBranch(propBranch || { _id: propBranchId, branchName: propBranchName });
      } else if (!selectedBranch && userInfo?.branchPk) {
        const userBranch = allBranchesList?.find(b => b._id === userInfo.branchPk);
        setSelectedBranch(userBranch || null);
      }
      fetchProducts();
    }
  }, [open, propBranchId]);

  const fetchProducts = (branchId) => {
    const branch = branchId || selectedBranch?._id || effectiveBranchId;
    if (!branch) return;
    
    setLoading(true);
    ProductListFilterAPICall(
      {
        branchList: [branch],
        search: searchTerm || '',
        index: 0,
        noPagination: true  // Fetch all products for mobile view
      },
      setLoading
    );
  };

  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
    if (newValue) {
      fetchProducts(newValue._id);
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const formatAmount = (amount) => {
    return Number(amount || 0).toFixed(decimalPosition);
  };

  // Group products by POS Category
  const getGroupedProducts = () => {
    const products = allProductsList?.list || [];
    const grouped = {};
    
    products.forEach(product => {
      const catName = product.POSCATNAME || 'Uncategorized';
      if (!grouped[catName]) {
        grouped[catName] = [];
      }
      grouped[catName].push(product);
    });
    
    return grouped;
  };

  const groupedProducts = getGroupedProducts();
  const categoryNames = Object.keys(groupedProducts);

  const renderProducts = () => {
    if (!allProductsList?.list?.length) {
      return (
        <div className="mobile-report-empty">
          <InboxIcon />
          <p>No products found.<br />{searchTerm ? 'Try a different search term.' : 'No products available.'}</p>
        </div>
      );
    }

    return categoryNames.map((catName, idx) => {
      const isExpanded = expandedCategories[catName] !== false;
      const products = groupedProducts[catName] || [];

      return (
        <div className="mobile-report-card" key={catName || idx}>
          <div 
            className="mobile-report-card-header category"
            onClick={() => toggleCategory(catName)}
          >
            <h4><CategoryIcon /> {catName}</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="total-badge">{products.length} items</span>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
          </div>
          <div className={`mobile-report-card-content ${isExpanded ? 'expanded' : ''}`}>
            {products.length > 0 ? (
              <table className="mobile-report-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-center">Stock</th>
                    <th className="text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, pIdx) => (
                    <tr key={product._id || pIdx}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{product.name}</div>
                        <div style={{ fontSize: '0.7rem', color: '#888' }}>{product.prodId}</div>
                        {product.UOMNAME && (
                          <div style={{ fontSize: '0.7rem', color: '#666' }}>
                            {product.UOMNAME}
                          </div>
                        )}
                      </td>
                      <td className="text-center" style={{ color: product.closingStock > 0 ? '#11998e' : '#f5576c' }}>
                        {Number(product.closingStock || 0).toFixed(2)}
                      </td>
                      <td className="text-right" style={{ fontWeight: 600 }}>
                        {formatAmount(product.cost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '12px' }}>
                No products in this category
              </p>
            )}
          </div>
        </div>
      );
    });
  };

  const getTotalProducts = () => {
    return allProductsList?.list?.length || 0;
  };

  const getTotalStockValue = () => {
    return allProductsList?.list?.reduce((sum, p) => sum + (Number(p.stockvalue) || 0), 0) || 0;
  };

  // PDF Generation
  const generatePdfPreview = async () => {
    if (!printRef.current) return;
    
    setPdfLoading(true);
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const blob = pdf.output('blob');
      const dataUrl = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfDataUrl(dataUrl);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `@page { size: A4; margin: 10mm; }`
  });

  const sharePDF = async () => {
    if (!pdfBlob) return;
    const fileName = `ProductList_${selectedBranch?.branchName || 'All'}.pdf`;
    
    if (navigator.share && navigator.canShare) {
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'Product List Report' });
          return;
        } catch (err) {
          if (err.name !== 'AbortError') console.error('Share failed:', err);
        }
      }
    }
    downloadPDF();
  };

  const downloadPDF = () => {
    if (!pdfDataUrl) return;
    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = `ProductList_${selectedBranch?.branchName || 'All'}.pdf`;
    link.click();
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfDataUrl) URL.revokeObjectURL(pdfDataUrl);
    setPdfDataUrl(null);
    setPdfBlob(null);
  };

  // Render Professional A4 Print Template
  const renderPrintTemplate = () => {
    const products = allProductsList?.list || [];
    if (!products.length) return null;

    const headerStyle = {
      backgroundColor: '#edf2f9',
      padding: '8px 10px',
      fontWeight: 'bold',
      fontSize: '11px',
      borderBottom: '1px solid #ddd'
    };

    const cellStyle = {
      padding: '6px 10px',
      fontSize: '10px',
      borderBottom: '1px solid #eee'
    };

    return (
      <div ref={printRef} style={{ width: '210mm', padding: '15px', backgroundColor: 'white', fontFamily: 'Arial, sans-serif' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '15px', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
          {companyProfile?.logo && (
            <img src={companyProfile.logo} alt="Logo" style={{ height: '40px', marginBottom: '8px' }} />
          )}
          <h2 style={{ margin: '0 0 5px 0', fontSize: '16px' }}>{companyProfile?.companyName || 'Restaurant'}</h2>
          <h3 style={{ margin: '10px 0 5px 0', fontSize: '14px' }}>Product List</h3>
          <p style={{ margin: 0, fontSize: '11px' }}>
            <strong>Branch:</strong> {selectedBranch?.branchName || 'All Branches'} | 
            <strong> Date:</strong> {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>
        </div>

        {/* Summary */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <span><strong>Total Products:</strong> {products.length}</span>
          <span><strong>Total Stock Value:</strong> {formatAmount(getTotalStockValue())}</span>
        </div>

        {/* Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, width: '12%', textAlign: 'left' }}>Product ID</th>
              <th style={{ ...headerStyle, width: '22%', textAlign: 'left' }}>Product Name</th>
              <th style={{ ...headerStyle, width: '12%', textAlign: 'left' }}>Attributes</th>
              <th style={{ ...headerStyle, width: '8%', textAlign: 'left' }}>UOM</th>
              <th style={{ ...headerStyle, width: '10%', textAlign: 'left' }}>Purchase UOM</th>
              <th style={{ ...headerStyle, width: '12%', textAlign: 'left' }}>POS Category</th>
              <th style={{ ...headerStyle, width: '10%', textAlign: 'left' }}>Barcode</th>
              <th style={{ ...headerStyle, width: '8%', textAlign: 'right', color: 'red' }}>Closing Stock</th>
              <th style={{ ...headerStyle, width: '6%', textAlign: 'right', color: 'blue' }}>Unit Cost</th>
              <th style={{ ...headerStyle, width: '8%', textAlign: 'right' }}>Stock Value</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, idx) => (
              <tr key={product._id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={cellStyle}>{product.prodId}</td>
                <td style={cellStyle}>{product.name}</td>
                <td style={cellStyle}>{product.attribute?.map(a => a.value).join(', ') || '-'}</td>
                <td style={cellStyle}>{product.UOMNAME || '-'}</td>
                <td style={cellStyle}>{product.PUCHASEUOMNAME || '-'}</td>
                <td style={cellStyle}>{product.POSCATNAME || '-'}</td>
                <td style={cellStyle}>{product.barcode || '-'}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: 'red' }}>{Number(product.closingStock || 0).toFixed(2)}</td>
                <td style={{ ...cellStyle, textAlign: 'right', color: 'blue' }}>{formatAmount(product.cost)}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>{formatAmount(product.stockvalue)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Footer */}
        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9px', color: '#888', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
          Generated on {new Date().toLocaleString('en-IN')}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="mobile-report-dialog"
    >
      <div className="mobile-report-header">
        <h3>Product List</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {allProductsList?.list?.length > 0 && (
            <IconButton 
              onClick={generatePdfPreview} 
              size="small" 
              disabled={pdfLoading}
              title="PDF Preview"
            >
              {pdfLoading ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            </IconButton>
          )}
          <IconButton className="close-btn" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="mobile-report-container">
        {/* Branch Selector */}
        <div className="mobile-report-filters">
          <div className="filter-row">
            <Autocomplete
              options={allBranchesList || []}
              getOptionLabel={(option) => option?.branchName || ''}
              value={selectedBranch}
              onChange={handleBranchChange}
              size="small"
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Branch" InputLabelProps={{ shrink: true }} />
              )}
            />
          </div>
          <div className="filter-row">
            <TextField
              fullWidth
              size="small"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton onClick={handleSearch} color="primary" size="small">
              <SearchIcon />
            </IconButton>
          </div>
        </div>

        {/* Summary */}
        <div className="mobile-summary-grid">
          <div className="summary-item">
            <div className="label">Categories</div>
            <div className="value">{categoryNames.length}</div>
          </div>
          <div className="summary-item">
            <div className="label">Total Products</div>
            <div className="value">{getTotalProducts()}</div>
          </div>
          <div className="summary-item" style={{ gridColumn: 'span 2' }}>
            <div className="label">Total Stock Value</div>
            <div className="value" style={{ color: '#667eea' }}>{formatAmount(getTotalStockValue())}</div>
          </div>
        </div>

        {loading ? (
          <div className="mobile-report-loading">
            <CircularProgress size={40} />
            <p>Loading products...</p>
          </div>
        ) : (
          renderProducts()
        )}
      </div>

      {/* PDF Preview Dialog */}
      <Dialog
        open={showPdfPreview}
        onClose={closePdfPreview}
        fullWidth
        maxWidth="md"
        className="pdf-preview-dialog"
      >
        <div className="pdf-preview-header">
          <h3>Print Preview</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton onClick={handlePrint} size="small" title="Print">
              <PrintIcon />
            </IconButton>
            <IconButton onClick={sharePDF} size="small" title="Share">
              <ShareIcon />
            </IconButton>
            <IconButton onClick={downloadPDF} size="small" title="Download">
              <PictureAsPdfIcon />
            </IconButton>
            <IconButton onClick={closePdfPreview} size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className="pdf-preview-content">
          {pdfDataUrl && (
            <iframe src={pdfDataUrl} title="PDF Preview" className="pdf-iframe" />
          )}
        </div>
      </Dialog>

      {/* Hidden Professional A4 Print Template */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        {renderPrintTemplate()}
      </div>
    </Dialog>
  );
};

export default MobileProductList;
